import { CommonHelperFunctions, ICommentDto } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export interface ICommentAttachmentUploadResult {
  url: string;
  name: string;
}

export class CommentService {
  public static async Create(obj: ICommentDto): Promise<AxiosResponse> {
    try {
      // Identity and authorship are server-owned: the backend generates the comment Id and
      // links the author from the auth context. The client's placeholder `id` and `createdUser`
      // carry empty-string GUIDs (""), which the backend's strict System.Text.Json cannot bind
      // to its non-nullable `Guid` fields — that fails the request with 400 before the action
      // even runs. Send only the client-owned fields.
      const payload: Partial<ICommentDto> = { ...obj };
      delete payload.id;
      delete payload.createdUser;
      return await Axios.post<Partial<ICommentDto>, AxiosResponse>(`${backendUrl}/comments/create`, payload);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Update(obj: ICommentDto): Promise<AxiosResponse> {
    try {
      return await Axios.put<ICommentDto, AxiosResponse>(`${backendUrl}/comments/update`, obj);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Delete(id: string): Promise<AxiosResponse> {
    try {
      return await Axios.delete<ICommentDto, AxiosResponse>(`${backendUrl}/comments/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAllByScreenDesignId(id: string): Promise<AxiosResponse<ICommentDto[]>> {
    try {
      return await Axios.get<string, AxiosResponse>(`${backendUrl}/comments/getallbyscreendesignid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Resolve(id: string): Promise<AxiosResponse> {
    try {
      return await Axios.put<void, AxiosResponse>(`${backendUrl}/comments/resolve/${id}`, {});
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Reopen(id: string): Promise<AxiosResponse> {
    try {
      return await Axios.put<void, AxiosResponse>(`${backendUrl}/comments/reopen/${id}`, {});
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ToggleReaction(commentId: string, emoji: string): Promise<AxiosResponse> {
    try {
      return await Axios.post<ICommentReactionRequest, AxiosResponse>(`${backendUrl}/comments/togglereaction`, {
        commentId,
        emoji
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async UploadAttachment(file: File): Promise<AxiosResponse<ICommentAttachmentUploadResult>> {
    try {
      const formData: FormData = new FormData();
      formData.append("File", file);
      return await Axios.post<FormData, AxiosResponse<ICommentAttachmentUploadResult>>(
        `${backendUrl}/comments/uploadattachment`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}

interface ICommentReactionRequest {
  commentId: string;
  emoji: string;
}
